<template>
    <div class="request-types-page requests-glass argus-glass-page">
        <div class="request-types-header">
            <div>
                <h4 class="mb-1">Типы заявок</h4>
                <div class="text-muted small">
                    Найдено: {{ totalRows }} из {{ requestTypes.length }}
                    <span class="mx-1">·</span>
                    активных: {{ activeRowsCount }}
                    <span class="mx-1">·</span>
                    неактивных: {{ inactiveRowsCount }}
                </div>
            </div>
            <b-button
                variant="success"
                size="sm"
                @click="openCreateModal"
                v-if="$can('create', 'requestTypes')"
            >
                <i class="fas fa-plus mr-1"></i>
                Создать тип заявки
            </b-button>
        </div>

        <div class="request-types-toolbar">
            <b-input-group size="sm" class="request-types-search">
                <b-input-group-prepend is-text>
                    <i class="fas fa-search"></i>
                </b-input-group-prepend>
                <b-form-input
                    v-model.trim="filters.q"
                    type="search"
                    placeholder="Поиск по названию, описанию, ID, процессу"
                />
                <b-input-group-append v-if="filters.q">
                    <b-button
                        variant="outline-secondary"
                        title="Очистить поиск"
                        @click="clearSearch"
                    >
                        <i class="fas fa-times"></i>
                    </b-button>
                </b-input-group-append>
            </b-input-group>

            <b-form-select
                v-model="filters.category"
                :options="categoryFilterOptions"
                size="sm"
            />

            <b-form-select
                v-model="filters.status"
                :options="statusFilterOptions"
                size="sm"
            />

            <b-form-select
                v-model="filters.process"
                :options="processFilterOptions"
                size="sm"
            />
        </div>

        <div v-if="activeFilterItems.length" class="request-types-active-filters">
            <span class="request-types-active-filters__label">
                <i class="fas fa-filter"></i>
                Включены фильтры:
            </span>
            <button
                v-for="item in activeFilterItems"
                :key="item.key"
                type="button"
                class="request-types-active-filter"
                :title="`Снять фильтр: ${item.text}`"
                @click="clearFilterItem(item.key)"
            >
                <span class="request-types-active-filter__text">{{ item.text }}</span>
                <i class="fas fa-times" aria-hidden="true"></i>
            </button>
            <button
                type="button"
                class="request-types-active-filters__reset"
                @click="resetFilters"
            >
                Сбросить все
            </button>
        </div>

        <b-table
            class="request-types-table"
            responsive="lg"
            :fields="fields"
            :items="filteredRequestTypes"
            :current-page="currentPage"
            :per-page="perPage"
            :busy="loading"
            :tbody-tr-class="requestTypeRowClass"
            small
            hover
            show-empty
            empty-text="Типы заявок не найдены"
        >
            <template #table-busy>
                <div class="text-center text-muted py-4">
                    <b-spinner small class="mr-2"></b-spinner>
                    Загрузка типов заявок
                </div>
            </template>

            <template #cell(name)="data">
                <div class="request-type-main">
                    <span
                        class="request-type-color-dot"
                        :class="getRequestTypeColorClass(data.item)"
                        :title="getRequestTypeColor(data.item)"
                    ></span>

                    <div class="request-type-main__body">
                        <div class="request-type-main__title-row">
                            <i
                                class="request-type-list-icon"
                                :class="getRequestTypeIcon(data.item)"
                                :title="getRequestTypeIconTitle(data.item)"
                            ></i>
                            <a
                                v-if="$can('update', 'requestTypes')"
                                href=""
                                class="request-type-main__title"
                                @click.prevent.stop="openEditModal(data.item)"
                            >
                                {{ data.item.name }}
                            </a>
                            <span v-else class="request-type-main__title">
                                {{ data.item.name }}
                            </span>
                            <b-badge
                                :variant="data.item.is_active ? 'success' : 'secondary'"
                                class="request-type-main__status"
                            >
                                {{ data.item.is_active ? 'Активен' : 'Неактивен' }}
                            </b-badge>
                        </div>

                        <div class="request-type-main__meta">
                            <span>#{{ data.item.id }}</span>
                            <span>
                                <i :class="getCategoryIcon(data.item.category)" aria-hidden="true"></i>
                                {{ getCategoryText(data.item.category) }}
                            </span>
                        </div>

                        <div
                            v-if="data.item.description"
                            class="request-type-main__description"
                            :title="data.item.description"
                        >
                            {{ data.item.description }}
                        </div>
                    </div>
                </div>
            </template>

            <template #cell(participants)="data">
                <div class="request-type-participants">
                    <div
                        v-for="row in getParticipantRows(data.item)"
                        :key="row.key"
                        class="request-type-participant-row"
                        :class="`request-type-participant-row--${row.key}`"
                    >
                        <span class="request-type-participant-row__icon" aria-hidden="true">
                            <i :class="row.icon"></i>
                        </span>
                        <div class="request-type-participant-row__content">
                            <div class="request-type-participant-row__label">
                                {{ row.label }}
                            </div>
                            <div
                                class="request-type-participant-row__value-wrap"
                                :class="{ 'request-type-participant-row__value-wrap--interactive': hasParticipantUserTooltip(row) }"
                                @mouseenter="loadParticipantUsers(row)"
                            >
                                <div class="request-type-participant-row__value">
                                    {{ row.text }}
                                </div>
                                <div
                                    v-if="hasParticipantUserTooltip(row)"
                                    class="request-type-participant-tooltip"
                                >
                                    <div class="request-type-participant-tooltip__title">
                                        Конкретные пользователи
                                    </div>
                                    <div
                                        v-if="usersLoading && !allUsers.length"
                                        class="request-type-participant-tooltip__muted"
                                    >
                                        Загрузка...
                                    </div>
                                    <div
                                        v-else-if="usersLoadError"
                                        class="request-type-participant-tooltip__muted"
                                    >
                                        Не удалось загрузить список
                                    </div>
                                    <div v-else class="request-type-participant-tooltip__list">
                                        <div
                                            v-for="user in getParticipantTooltipUsers(row)"
                                            :key="user.id"
                                            class="request-type-participant-tooltip__user"
                                        >
                                            {{ user.name }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <template #cell(workflow)="data">
                <div class="request-type-workflow">
                    <div class="request-type-workflow__headline">
                        <i class="fas fa-route" aria-hidden="true"></i>
                        {{ getProcessHeadline(data.item) }}
                    </div>

                    <div class="request-type-workflow__chips">
                        <span
                            v-for="badge in getProcessBadges(data.item)"
                            :key="badge.key"
                            class="request-type-chip"
                            :class="`request-type-chip--${badge.variant}`"
                            :title="badge.title || badge.text"
                        >
                            <i v-if="badge.icon" :class="badge.icon" aria-hidden="true"></i>
                            {{ badge.text }}
                        </span>
                    </div>

                    <div
                        v-if="getProcessDetails(data.item).length"
                        class="request-type-workflow__details"
                    >
                        <div
                            v-for="detail in getProcessDetails(data.item)"
                            :key="detail.key"
                            class="request-type-workflow-detail"
                        >
                            <span class="request-type-workflow-detail__label">
                                {{ detail.label }}
                            </span>
                            <span
                                class="request-type-workflow-detail__value"
                                :title="detail.title || detail.text"
                            >
                                {{ detail.text }}
                            </span>
                        </div>
                    </div>
                </div>
            </template>

            <template #cell(actions)="data">
                <div class="request-type-actions">
                    <b-button
                        v-if="$can('create', 'requestTypes')"
                        size="sm"
                        variant="outline-primary"
                        class="request-type-action-button"
                        :disabled="copyingRequestTypeId === data.item.id"
                        title="Копировать"
                        @click.stop="copyRequestType(data.item)"
                    >
                        <i class="fas fa-copy"></i>
                    </b-button>
                    <b-button
                        v-if="$can('update', 'requestTypes')"
                        size="sm"
                        variant="outline-success"
                        class="request-type-action-button"
                        title="Редактировать"
                        @click.stop="openEditModal(data.item)"
                    >
                        <i class="fas fa-pen"></i>
                    </b-button>
                    <b-button
                        v-if="$can('delete', 'requestTypes')"
                        size="sm"
                        variant="outline-danger"
                        class="request-type-action-button"
                        title="Удалить"
                        @click.stop="deleteRequestType(data.item)"
                    >
                        <i class="fas fa-trash-alt"></i>
                    </b-button>
                </div>
            </template>
        </b-table>

        <div class="request-types-mobile-list">
            <div v-if="loading" class="text-center text-muted py-4">
                <b-spinner small class="mr-2"></b-spinner>
                Загрузка типов заявок
            </div>
            <div v-else-if="!filteredRequestTypes.length" class="text-center text-muted py-4">
                Типы заявок не найдены
            </div>
            <template v-else>
                <b-card
                    v-for="item in pagedRequestTypes"
                    :key="item.id"
                    :id="`request-type-mobile-card-${item.id}`"
                    no-body
                    class="request-type-mobile-card"
                    :class="{
                        'request-type-mobile-card--inactive': !item.is_active,
                        'request-type-mobile-card--return-flow': item.is_active && hasReturnFlow(item),
                        'request-type-mobile-card--highlight-target': isHighlightTargetRequestType(item)
                    }"
                >
                    <span
                        class="request-type-mobile-card__accent"
                        :class="getRequestTypeColorClass(item)"
                        aria-hidden="true"
                    ></span>

                    <div class="request-type-mobile-card__header">
                        <div class="request-type-mobile-card__title-block">
                            <div class="request-type-mobile-card__title-row">
                                <i
                                    class="request-type-list-icon"
                                    :class="getRequestTypeIcon(item)"
                                    :title="getRequestTypeIconTitle(item)"
                                ></i>
                                <a
                                    v-if="$can('update', 'requestTypes')"
                                    href=""
                                    class="request-type-mobile-card__title"
                                    @click.prevent.stop="openEditModal(item)"
                                >
                                    {{ item.name }}
                                </a>
                                <span v-else class="request-type-mobile-card__title">
                                    {{ item.name }}
                                </span>
                            </div>
                            <div class="request-type-mobile-card__meta">
                                <span>#{{ item.id }}</span>
                                <span>
                                    <i :class="getCategoryIcon(item.category)" aria-hidden="true"></i>
                                    {{ getCategoryText(item.category) }}
                                </span>
                            </div>
                        </div>

                        <b-badge :variant="item.is_active ? 'success' : 'secondary'">
                            {{ item.is_active ? 'Активен' : 'Неактивен' }}
                        </b-badge>
                    </div>

                    <div v-if="item.description" class="request-type-mobile-card__description">
                        {{ item.description }}
                    </div>

                    <div class="request-type-mobile-card__section">
                        <div class="request-type-mobile-card__section-title">
                            <i class="fas fa-users" aria-hidden="true"></i>
                            Участники
                        </div>
                        <div class="request-type-participants request-type-participants--mobile">
                            <div
                                v-for="row in getParticipantRows(item)"
                                :key="row.key"
                                class="request-type-participant-row"
                                :class="`request-type-participant-row--${row.key}`"
                            >
                                <span class="request-type-participant-row__icon" aria-hidden="true">
                                    <i :class="row.icon"></i>
                                </span>
                                <div class="request-type-participant-row__content">
                                    <div class="request-type-participant-row__label">
                                        {{ row.label }}
                                    </div>
                                <div
                                    class="request-type-participant-row__value-wrap"
                                    :class="{ 'request-type-participant-row__value-wrap--interactive': hasParticipantUserTooltip(row) }"
                                    @mouseenter="loadParticipantUsers(row)"
                                >
                                    <div class="request-type-participant-row__value">
                                        {{ row.text }}
                                    </div>
                                    <div
                                        v-if="hasParticipantUserTooltip(row)"
                                        class="request-type-participant-tooltip"
                                    >
                                        <div class="request-type-participant-tooltip__title">
                                            Конкретные пользователи
                                        </div>
                                        <div
                                            v-if="usersLoading && !allUsers.length"
                                            class="request-type-participant-tooltip__muted"
                                        >
                                            Загрузка...
                                        </div>
                                        <div
                                            v-else-if="usersLoadError"
                                            class="request-type-participant-tooltip__muted"
                                        >
                                            Не удалось загрузить список
                                        </div>
                                        <div v-else class="request-type-participant-tooltip__list">
                                            <div
                                                v-for="user in getParticipantTooltipUsers(row)"
                                                :key="user.id"
                                                class="request-type-participant-tooltip__user"
                                            >
                                                {{ user.name }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>

                    <div class="request-type-mobile-card__section">
                        <div class="request-type-mobile-card__section-title">
                            <i class="fas fa-route" aria-hidden="true"></i>
                            Процесс
                        </div>
                        <div class="request-type-workflow">
                            <div class="request-type-workflow__headline">
                                {{ getProcessHeadline(item) }}
                            </div>
                            <div class="request-type-workflow__chips">
                                <span
                                    v-for="badge in getProcessBadges(item)"
                                    :key="badge.key"
                                    class="request-type-chip"
                                    :class="`request-type-chip--${badge.variant}`"
                                    :title="badge.title || badge.text"
                                >
                                    <i v-if="badge.icon" :class="badge.icon" aria-hidden="true"></i>
                                    {{ badge.text }}
                                </span>
                            </div>
                            <div
                                v-if="getProcessDetails(item).length"
                                class="request-type-workflow__details"
                            >
                                <div
                                    v-for="detail in getProcessDetails(item)"
                                    :key="detail.key"
                                    class="request-type-workflow-detail"
                                >
                                    <span class="request-type-workflow-detail__label">
                                        {{ detail.label }}
                                    </span>
                                    <span class="request-type-workflow-detail__value">
                                        {{ detail.text }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="request-type-mobile-card__footer">
                        <b-button
                            v-if="$can('create', 'requestTypes')"
                            size="sm"
                            variant="outline-primary"
                            :disabled="copyingRequestTypeId === item.id"
                            title="Копировать"
                            @click.stop="copyRequestType(item)"
                        >
                            <i class="fas fa-copy"></i>
                        </b-button>
                        <b-button
                            v-if="$can('update', 'requestTypes')"
                            size="sm"
                            variant="outline-success"
                            title="Редактировать"
                            @click.stop="openEditModal(item)"
                        >
                            <i class="fas fa-pen"></i>
                        </b-button>
                        <b-button
                            v-if="$can('delete', 'requestTypes')"
                            size="sm"
                            variant="outline-danger"
                            title="Удалить"
                            @click.stop="deleteRequestType(item)"
                        >
                            <i class="fas fa-trash-alt"></i>
                        </b-button>
                    </div>
                </b-card>
            </template>
        </div>

        <div class="request-types-footer">
            <div class="request-types-footer__page-size">
                <span class="text-muted small">На странице</span>
                <b-form-select
                    v-model.number="perPage"
                    :options="pageSizeOptions"
                    size="sm"
                    class="request-types-page-size"
                />
            </div>

            <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                align="center"
                size="sm"
                class="mb-0"
            />
        </div>
    </div>
</template>

<script>
import { REQUEST_CATEGORIES, getRequestCategoryText } from '@/constants/requestCategories';
import { normalizeCardBorderVariant } from '@/constants/requestCardColors';
import { normalizeRequestTypeIcon, REQUEST_TYPE_ICON_OPTIONS } from '@/constants/requestTypeIcons';

const ITEM_CATALOG_LABELS = {
    materials: 'Материалы',
    employees: 'Сотрудники',
    fuel_cards: 'Топливные карты',
    mechanic_assets: 'Техника'
};

const MECHANIC_CATALOG_SCOPE_LABELS = {
    all: 'вся техника',
    mechanic_assets_on_object: 'техника на объекте'
};

const FUEL_CARD_OPERATION_LABELS = {
    assign_to_initiator: 'выдать инициатору',
    assign_to_action_user: 'выдать выполнившему',
    issue_to_recipient: 'выдать получателю',
    move_to_recipient: 'переместить карту',
    change_limits: 'изменить лимиты',
    attach_to_request_object: 'привязать к объекту',
    return_card: 'сдать карту',
    block_card: 'заблокировать',
    mark_lost: 'зафиксировать утерю'
};

const MECHANIC_OPERATION_LABELS = {
    issue_to_object: 'выдать на объект',
    move_to_object: 'переместить на объект',
    move_to_warehouse: 'переместить на склад',
    return_to_warehouse: 'вернуть на склад',
    accept_to_repair: 'принять в ремонт',
    finish_repair: 'завершить ремонт',
    rent_out: 'сдать в аренду',
    return_from_rent: 'вернуть из аренды'
};

const TRIGGER_STATUS_LABELS = {
    in_progress: 'взята в работу',
    mechanic_repair_accepted: 'принято в ремонт',
    completed: 'завершена',
    awaiting_acceptance: 'ожидает приемку',
    partially_completed: 'частично выполнена'
};

const AUTO_TRANSITION_LABELS = {
    submitted: 'после отправки',
    allApproved: 'после согласования',
    anyRejected: 'при отклонении'
};

const LIST_RETURN_SESSION_KEY = 'requestTypesListReturn';
const LIST_STATE_SESSION_KEY = 'requestTypesListState';
const LIST_RETURN_SESSION_TTL_MS = 60 * 60 * 1000;
const PAGE_SIZE_VALUES = [10, 20, 50, 100];
const SEARCH_STOP_WORDS = new Set([
    'а',
    'в',
    'во',
    'да',
    'для',
    'до',
    'за',
    'и',
    'из',
    'к',
    'ко',
    'на',
    'не',
    'о',
    'об',
    'от',
    'по',
    'с',
    'со',
    'у'
]);

function getDefaultFilters() {
    return {
        q: '',
        category: 'all',
        status: 'all',
        process: 'all'
    };
}

function readStoredListState(key) {
    try {
        if (typeof sessionStorage === 'undefined') {
            return null;
        }
        const raw = sessionStorage.getItem(key);
        if (!raw) {
            return null;
        }
        const data = JSON.parse(raw);
        if (!data || Date.now() - data.savedAt > LIST_RETURN_SESSION_TTL_MS) {
            return null;
        }
        return data;
    } catch (e) {
        return null;
    }
}

function getInitialListState() {
    const state = readStoredListState(LIST_RETURN_SESSION_KEY) ||
        readStoredListState(LIST_STATE_SESSION_KEY) ||
        {};
    const page = Number(state.page);
    const perPage = Number(state.perPage);

    return {
        filters: {
            ...getDefaultFilters(),
            ...(state.filters || {})
        },
        currentPage: Number.isInteger(page) && page > 0 ? page : 1,
        perPage: PAGE_SIZE_VALUES.includes(perPage) ? perPage : 20
    };
}

export default {
    name: "RequestTypes",
    data() {
        const initialListState = getInitialListState();
        return {
            filters: initialListState.filters,
            currentPage: initialListState.currentPage,
            perPage: initialListState.perPage,
            pageSizeOptions: PAGE_SIZE_VALUES.map(value => ({ value, text: String(value) })),
            fields: [
                {
                    key: 'name',
                    label: 'Тип заявки',
                    sortable: true,
                    tdClass: 'request-type-name-cell'
                },
                {
                    key: 'participants',
                    label: 'Участники',
                    sortable: false,
                    tdClass: 'request-type-participants-cell'
                },
                {
                    key: 'workflow',
                    label: 'Настройки процесса',
                    sortable: false,
                    tdClass: 'request-type-workflow-cell'
                },
                {
                    key: 'actions',
                    label: '',
                    class: 'text-center',
                    tdClass: 'request-type-actions-cell'
                }
            ],
            statusFilterOptions: [
                { value: 'all', text: 'Все статусы' },
                { value: 'active', text: 'Активные' },
                { value: 'inactive', text: 'Неактивные' }
            ],
            processFilterOptions: [
                { value: 'all', text: 'Все процессы' },
                { value: 'manual_executor', text: 'Ручное назначение' },
                { value: 'building_process', text: 'С объектом' },
                { value: 'item_catalog', text: 'С каталогом позиций' },
                { value: 'mechanic_action', text: 'Действия с техникой' },
                { value: 'fuel_card_action', text: 'Действия с ТК' },
                { value: 'linked_requests', text: 'Связанные заявки' },
                { value: 'required_attachments', text: 'Обязательные документы' },
                { value: 'returns', text: 'С возвратом' },
                { value: 'automation', text: 'Автоматика' }
            ],
            categoryOptions: REQUEST_CATEGORIES.map(({ value, text }) => ({ value, text })),
            allDepartments: [],
            allUsers: [],
            usersLoading: false,
            usersLoadError: null,
            usersLoadPromise: null,
            requestTypes: [],
            copyingRequestTypeId: null,
            highlightedRequestTypeId: null,
            restoringListState: false,
            loading: false
        }
    },
    computed: {
        categoryFilterOptions() {
            return [
                { value: 'all', text: 'Все категории' },
                ...this.categoryOptions
            ];
        },
        filteredRequestTypes() {
            const words = this.getSearchWords(this.filters.q, { ignoreStopWords: true });

            return this.requestTypes.filter(item => {
                if (this.filters.status === 'active' && !item.is_active) {
                    return false;
                }
                if (this.filters.status === 'inactive' && item.is_active) {
                    return false;
                }
                if (this.filters.category !== 'all' && item.category !== this.filters.category) {
                    return false;
                }
                if (!this.matchesProcessFilter(item, this.filters.process)) {
                    return false;
                }
                if (!words.length) {
                    return true;
                }

                const tokens = this.getSearchTokens(item);
                return words.every(word => this.matchesSearchWord(tokens, word));
            });
        },
        pagedRequestTypes() {
            const start = (this.currentPage - 1) * this.perPage;
            return this.filteredRequestTypes.slice(start, start + this.perPage);
        },
        totalRows() {
            return this.filteredRequestTypes.length;
        },
        activeRowsCount() {
            return this.filteredRequestTypes.filter(item => item.is_active).length;
        },
        inactiveRowsCount() {
            return this.filteredRequestTypes.filter(item => !item.is_active).length;
        },
        activeFilterItems() {
            const items = [];
            if (this.filters.q) {
                items.push({
                    key: 'q',
                    text: `Поиск: ${this.filters.q}`
                });
            }
            if (this.filters.category !== 'all') {
                items.push({
                    key: 'category',
                    text: this.getOptionText(this.categoryFilterOptions, this.filters.category)
                });
            }
            if (this.filters.status !== 'all') {
                items.push({
                    key: 'status',
                    text: this.getOptionText(this.statusFilterOptions, this.filters.status)
                });
            }
            if (this.filters.process !== 'all') {
                items.push({
                    key: 'process',
                    text: this.getOptionText(this.processFilterOptions, this.filters.process)
                });
            }
            return items;
        }
    },
    watch: {
        filters: {
            deep: true,
            handler() {
                if (this.restoringListState) {
                    return;
                }
                this.currentPage = 1;
                this.saveListState();
            }
        },
        currentPage() {
            if (this.restoringListState) {
                return;
            }
            this.saveListState();
        },
        perPage() {
            if (this.restoringListState) {
                return;
            }
            this.currentPage = 1;
            this.saveListState();
        }
    },
    methods: {
        async fetchTypes() {
            this.loading = !this.requestTypes.length;
            try {
                const data = await this.$store.dispatch('requests_getRequestTypes');
                this.requestTypes = this.normalizeRequestTypes(data);
            } catch (e) {
                console.error('Ошибка загрузки типов заявок:', e);
                this.$bvToast.toast('Не удалось загрузить типы заявок', { title: 'Ошибка', variant: 'danger', solid: true });
            } finally {
                this.loading = false;
            }
        },
        normalizeRequestTypes(types) {
            return (types || []).map(rt => ({
                ...rt,
                status_config: rt.status_config || {}
            }));
        },
        hydrateRequestTypesFromStore() {
            const types = this.$store.state.requests && this.$store.state.requests.requestTypes;
            if (Array.isArray(types) && types.length) {
                this.requestTypes = this.normalizeRequestTypes(types);
            }
        },
        getCategoryText(category) {
            return getRequestCategoryText(category);
        },
        getCategoryIcon(category) {
            const option = REQUEST_CATEGORIES.find(item => item.value === category);
            return option ? option.icon : 'fas fa-ellipsis-h';
        },
        getWorkflowConfig(item) {
            return item && item.workflow_config && typeof item.workflow_config === 'object'
                ? item.workflow_config
                : {};
        },
        getRequestTypeColor(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            return normalizeCardBorderVariant(
                workflowConfig.cardBorderVariant,
                workflowConfig.cardBorderColor
            );
        },
        getRequestTypeColorClass(item) {
            return `bg-${this.getRequestTypeColor(item)}`;
        },
        getRequestTypeIcon(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            return normalizeRequestTypeIcon(workflowConfig.requestTypeIcon);
        },
        getRequestTypeIconTitle(item) {
            const icon = this.getRequestTypeIcon(item);
            const option = REQUEST_TYPE_ICON_OPTIONS.find(({ value }) => value === icon);
            return option ? option.text : 'Иконка заявки';
        },
        getItemCatalogConfig(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            return workflowConfig.itemCatalog || workflowConfig.item_catalog || {};
        },
        isItemCatalogEnabled(item) {
            const config = this.getItemCatalogConfig(item);
            return Boolean(config && config.enabled === true);
        },
        isListItemsPreviewEnabled(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            const previewConfig = workflowConfig.listItemsPreview ||
                workflowConfig.list_items_preview ||
                {};
            return previewConfig.enabled === true ||
                workflowConfig.showItemsInList === true ||
                workflowConfig.show_items_in_list === true;
        },
        getBuildingProcessConfig(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            const source = workflowConfig.buildingProcess || workflowConfig.building_process || null;
            if (source && typeof source === 'object' && !Array.isArray(source)) {
                const required = source.required === true ||
                    source.isRequired === true ||
                    source.required_for_create === true;
                const enabled = source.enabled !== undefined
                    ? source.enabled === true
                    : (source.show !== undefined
                        ? source.show === true
                        : required);

                return {
                    enabled: enabled || required,
                    required
                };
            }

            if (workflowConfig.requiresBuildingProcess !== undefined) {
                const required = Boolean(workflowConfig.requiresBuildingProcess);
                return { enabled: required, required };
            }
            if (workflowConfig.requires_building_process !== undefined) {
                const required = Boolean(workflowConfig.requires_building_process);
                return { enabled: required, required };
            }
            const required = Boolean(item && item.category === 'materials');
            return { enabled: required, required };
        },
        getLinkedRequestsConfig(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            return workflowConfig.linkedRequests || workflowConfig.linked_requests || {};
        },
        isLinkedRequestsEnabled(item) {
            const config = this.getLinkedRequestsConfig(item);
            return config.enabled === true && Array.isArray(config.rules) && config.rules.length > 0;
        },
        getLinkedRequestRulesCount(item) {
            const config = this.getLinkedRequestsConfig(item);
            return Array.isArray(config.rules)
                ? config.rules.filter(rule => rule && rule.active !== false && rule.enabled !== false).length
                : 0;
        },
        getRequiredAttachmentsConfig(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            return workflowConfig.requiredAttachments || workflowConfig.required_attachments || {};
        },
        isRequiredAttachmentsEnabled(item) {
            const config = this.getRequiredAttachmentsConfig(item);
            return config.enabled === true && Array.isArray(config.rules) && config.rules.length > 0;
        },
        isRequiredAttachmentsPreviewEnabled(item) {
            const config = this.getRequiredAttachmentsConfig(item);
            const previewConfig = config.listPreview || config.list_preview || {};
            return config.enabled === true &&
                Array.isArray(config.rules) &&
                config.rules.length > 0 &&
                (
                    previewConfig.enabled === true ||
                    config.showInList === true ||
                    config.show_in_list === true
                );
        },
        getRequiredAttachmentRulesCount(item) {
            const config = this.getRequiredAttachmentsConfig(item);
            return Array.isArray(config.rules)
                ? config.rules.filter(rule => rule && rule.active !== false && rule.enabled !== false).length
                : 0;
        },
        getFuelCardActionConfig(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            return workflowConfig.fuelCardAction || workflowConfig.fuel_card_action || {};
        },
        isFuelCardActionEnabled(item) {
            const action = this.getFuelCardActionConfig(item);
            return action && action.enabled === true;
        },
        getMechanicAssetActions(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            const legacyAction = workflowConfig.mechanicAssetAction ||
                workflowConfig.mechanic_asset_action ||
                {};
            const actions = workflowConfig.mechanicAssetActions ||
                workflowConfig.mechanic_asset_actions ||
                [];
            const candidates = Array.isArray(actions) && actions.length
                ? actions
                : (legacyAction && Object.keys(legacyAction).length ? [legacyAction] : []);

            return candidates.filter(action => action && (
                action.enabled === true ||
                legacyAction.enabled === true
            ));
        },
        isMechanicAssetActionEnabled(item) {
            return this.getMechanicAssetActions(item).length > 0;
        },
        getEmployeeCatalogSubjectConfig(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            return workflowConfig.employeeCatalogSubject ||
                workflowConfig.employee_catalog_subject ||
                {};
        },
        isEmployeeCatalogSubjectEnabled(item) {
            const config = this.getEmployeeCatalogSubjectConfig(item);
            return config && config.enabled === true;
        },
        isLateApprovalEnabled(item) {
            return Boolean(item && item.late_approval_config && item.late_approval_config.enabled === true);
        },
        isAcceptanceRequired(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            return workflowConfig.requiresAcceptance !== false &&
                workflowConfig.requires_acceptance !== false;
        },
        hasAutoTransitions(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            const transitions = workflowConfig.autoTransitions || workflowConfig.auto_transitions || {};
            return Object.keys(transitions).some(key => transitions[key] === true || Boolean(transitions[key]));
        },
        hasDeadlineAutomation(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            const deadlines = workflowConfig.deadlinesConfig || workflowConfig.deadlines_config || {};
            return deadlines.staleRequestReminderEnabled === true ||
                deadlines.autoCloseAwaitingAcceptanceEnabled === true;
        },
        hasReturnFlow(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            const stateMachine = workflowConfig.stateMachineConfig || workflowConfig.state_machine_config || {};
            const actionToStatus = stateMachine.actionToStatus || stateMachine.action_to_status || {};
            const transitionsMap = stateMachine.transitionsMap || stateMachine.transitions_map || {};
            const allowedActions = Array.isArray(stateMachine.allowedActions)
                ? stateMachine.allowedActions
                : (Array.isArray(workflowConfig.allowedActions) ? workflowConfig.allowedActions : []);

            if (actionToStatus.return === 'returned' || allowedActions.includes('return')) {
                return true;
            }

            return Object.keys(transitionsMap).some(status => {
                const targets = transitionsMap[status];
                return Array.isArray(targets) && targets.includes('returned');
            });
        },
        hasExecutorStage(item) {
            return this.hasParticipantsConfig(item && item.executors) ||
                Boolean(item && item.executor_assignment_manual);
        },
        getProcessHeadline(item) {
            const hasApprovers = this.hasParticipantsConfig(item && item.approvers);
            const hasExecutors = this.hasExecutorStage(item);
            const requiresAcceptance = this.isAcceptanceRequired(item);

            if (hasApprovers && hasExecutors) {
                return requiresAcceptance
                    ? 'Согласование -> исполнение -> приемка'
                    : 'Согласование -> исполнение без приемки';
            }
            if (!hasApprovers && hasExecutors) {
                return requiresAcceptance
                    ? 'Без согласования -> исполнение -> приемка'
                    : 'Без согласования -> исполнение';
            }
            if (hasApprovers && !hasExecutors) {
                return 'Согласование без этапа исполнения';
            }
            return 'Создание без маршрута согласования';
        },
        getProcessBadges(item) {
            const badges = [];
            const buildingProcess = this.getBuildingProcessConfig(item);

            if (item && item.executor_assignment_manual) {
                badges.push({
                    key: 'manual-executor',
                    text: 'Исполнитель вручную',
                    variant: 'info',
                    icon: 'fas fa-hand-pointer'
                });
            }

            if (buildingProcess.enabled) {
                badges.push({
                    key: 'building-process',
                    text: buildingProcess.required ? 'Объект обязателен' : 'Объект опционально',
                    variant: buildingProcess.required ? 'danger' : 'primary',
                    icon: 'fas fa-hard-hat'
                });
            }

            if (this.isItemCatalogEnabled(item)) {
                const catalog = this.getItemCatalogConfig(item);
                badges.push({
                    key: 'item-catalog',
                    text: `Каталог: ${this.getItemCatalogTypeLabel(catalog.type)}`,
                    variant: 'primary',
                    icon: 'fas fa-list-ul'
                });
            }

            if (this.isMechanicAssetActionEnabled(item)) {
                const count = this.getMechanicAssetActions(item).length;
                badges.push({
                    key: 'mechanic-action',
                    text: count > 1 ? `Действия техники: ${count}` : 'Действие техники',
                    variant: 'warning',
                    icon: 'fas fa-tools'
                });
            }

            if (this.isFuelCardActionEnabled(item)) {
                badges.push({
                    key: 'fuel-card-action',
                    text: 'Действие ТК',
                    variant: 'warning',
                    icon: 'fas fa-credit-card'
                });
            }

            if (this.isEmployeeCatalogSubjectEnabled(item)) {
                badges.push({
                    key: 'employee-subject',
                    text: 'Заявка по сотруднику',
                    variant: 'info',
                    icon: 'fas fa-user'
                });
            }

            if (this.isLinkedRequestsEnabled(item)) {
                badges.push({
                    key: 'linked-requests',
                    text: `Связи: ${this.getLinkedRequestRulesCount(item)}`,
                    variant: 'info',
                    icon: 'fas fa-link'
                });
            }

            if (this.isRequiredAttachmentsEnabled(item)) {
                badges.push({
                    key: 'required-attachments',
                    text: `Обязательные файлы: ${this.getRequiredAttachmentRulesCount(item)}`,
                    variant: 'danger',
                    icon: 'fas fa-paperclip'
                });
            }

            if (this.isListItemsPreviewEnabled(item)) {
                badges.push({
                    key: 'items-preview',
                    text: 'Позиции в списке',
                    variant: 'secondary',
                    icon: 'fas fa-eye'
                });
            }

            if (this.isRequiredAttachmentsPreviewEnabled(item)) {
                badges.push({
                    key: 'attachments-preview',
                    text: 'Документы в списке',
                    variant: 'secondary',
                    icon: 'fas fa-eye'
                });
            }

            if (this.hasReturnFlow(item)) {
                badges.push({
                    key: 'return-flow',
                    text: 'Возврат',
                    variant: 'warning',
                    icon: 'fas fa-undo'
                });
            }

            if (this.hasAutoTransitions(item)) {
                badges.push({
                    key: 'auto-transitions',
                    text: 'Автопереходы',
                    variant: 'success',
                    icon: 'fas fa-magic'
                });
            }

            if (this.hasDeadlineAutomation(item)) {
                badges.push({
                    key: 'deadline-automation',
                    text: 'Контроль сроков',
                    variant: 'success',
                    icon: 'fas fa-clock'
                });
            }

            if (!this.isAcceptanceRequired(item) && this.hasExecutorStage(item)) {
                badges.push({
                    key: 'no-acceptance',
                    text: 'Без приемки',
                    variant: 'secondary',
                    icon: 'fas fa-check-double'
                });
            }

            if (item && item.is_emergency) {
                badges.push({
                    key: 'emergency',
                    text: 'Аварийная',
                    variant: 'danger',
                    icon: 'fas fa-exclamation-triangle'
                });
            } else if (item && item.submission_deadline_days) {
                badges.push({
                    key: 'deadline',
                    text: `${item.submission_deadline_days} раб. дн.`,
                    variant: item.requires_justification_if_late ? 'warning' : 'secondary',
                    icon: 'fas fa-calendar-day'
                });
            }

            if (this.isLateApprovalEnabled(item)) {
                badges.push({
                    key: 'late-approval',
                    text: 'Позднее согласование',
                    variant: 'warning',
                    icon: 'fas fa-user-clock'
                });
            }

            return badges;
        },
        getProcessDetails(item) {
            const details = [];
            const catalogSummary = this.getCatalogSummary(item);
            const actionSummary = this.getActionSummary(item);
            const deadlineSummary = this.getDeadlineSummary(item);
            const automationSummary = this.getAutomationSummary(item);
            const requiredFilesSummary = this.getRequiredFilesSummary(item);
            const editingSummary = this.getEditingSummary(item);

            if (catalogSummary) {
                details.push({
                    key: 'catalog',
                    label: 'Каталог',
                    text: catalogSummary
                });
            }
            if (actionSummary) {
                details.push({
                    key: 'actions',
                    label: 'Действия',
                    text: actionSummary
                });
            }
            if (deadlineSummary) {
                details.push({
                    key: 'deadlines',
                    label: 'Сроки',
                    text: deadlineSummary
                });
            }
            if (automationSummary) {
                details.push({
                    key: 'automation',
                    label: 'Автоматика',
                    text: automationSummary
                });
            }
            if (requiredFilesSummary) {
                details.push({
                    key: 'required-files',
                    label: 'Обязательные файлы',
                    text: requiredFilesSummary
                });
            }
            if (editingSummary) {
                details.push({
                    key: 'editing',
                    label: 'Правки',
                    text: editingSummary
                });
            }

            return details;
        },
        getItemCatalogTypeLabel(type) {
            return ITEM_CATALOG_LABELS[type] || type || 'Позиции';
        },
        getCatalogSummary(item) {
            if (!this.isItemCatalogEnabled(item)) {
                return '';
            }

            const catalog = this.getItemCatalogConfig(item);
            const parts = [this.getItemCatalogTypeLabel(catalog.type)];

            if (catalog.type === 'materials') {
                if (Array.isArray(catalog.material_group_ids) && catalog.material_group_ids.length) {
                    parts.push(`групп: ${catalog.material_group_ids.length}`);
                }
                if (Array.isArray(catalog.excluded_material_group_ids) && catalog.excluded_material_group_ids.length) {
                    parts.push(`исключений: ${catalog.excluded_material_group_ids.length}`);
                }
            }

            if (catalog.type === 'mechanic_assets') {
                parts.push(MECHANIC_CATALOG_SCOPE_LABELS[catalog.scope] || 'вся техника');
            }

            parts.push(catalog.allow_custom_items ? 'свободные позиции разрешены' : 'только каталог');

            if (this.isListItemsPreviewEnabled(item)) {
                parts.push('показывать в списке');
            }

            return parts.filter(Boolean).join(', ');
        },
        getActionSummary(item) {
            const parts = [];

            if (this.isFuelCardActionEnabled(item)) {
                const action = this.getFuelCardActionConfig(item);
                const operation = FUEL_CARD_OPERATION_LABELS[action.operation] || action.operation || 'операция не выбрана';
                const trigger = TRIGGER_STATUS_LABELS[action.triggerStatus] || action.triggerStatus || 'статус не выбран';
                parts.push(`ТК: ${operation}, когда ${trigger}`);
            }

            if (this.isMechanicAssetActionEnabled(item)) {
                const workflowConfig = this.getWorkflowConfig(item);
                const tableMode = workflowConfig.mechanicAssetTableMode ||
                    workflowConfig.mechanic_asset_table_mode ||
                    'movement';
                const modeLabel = tableMode === 'repair' ? 'ремонт' : 'перемещение';
                const actions = this.getMechanicAssetActions(item).map(action => {
                    const operation = MECHANIC_OPERATION_LABELS[action.operation] || action.operation || 'операция не выбрана';
                    const trigger = TRIGGER_STATUS_LABELS[action.triggerStatus] || action.triggerStatus || 'статус не выбран';
                    return `${operation} (${trigger})`;
                });
                parts.push(`Техника: ${modeLabel}; ${actions.join('; ')}`);
            }

            if (this.isEmployeeCatalogSubjectEnabled(item)) {
                parts.push('заявка привязана к выбранному сотруднику');
            }

            return parts.join(' | ');
        },
        getDeadlineSummary(item) {
            if (!item) {
                return '';
            }

            const parts = [];
            if (item.is_emergency) {
                parts.push('аварийная без ограничения срока подачи');
            } else if (item.submission_deadline_days) {
                parts.push(`${item.submission_deadline_days} рабочих дней на подачу`);
                if (item.requires_justification_if_late) {
                    parts.push('после срока нужно обоснование');
                }
            }

            if (this.isLateApprovalEnabled(item)) {
                parts.push('включено позднее согласование');
            }

            return parts.join(', ');
        },
        getAutomationSummary(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            const parts = [];
            const transitions = workflowConfig.autoTransitions || workflowConfig.auto_transitions || {};
            const enabledTransitions = Object.keys(transitions)
                .filter(key => transitions[key] === true || Boolean(transitions[key]))
                .map(key => AUTO_TRANSITION_LABELS[key] || key);

            if (enabledTransitions.length) {
                parts.push(`автопереходы: ${enabledTransitions.join(', ')}`);
            }

            const deadlines = workflowConfig.deadlinesConfig || workflowConfig.deadlines_config || {};
            if (deadlines.staleRequestReminderEnabled) {
                parts.push(`напоминание без движения: ${deadlines.staleCheckHours || '?'} ч`);
            }
            if (deadlines.autoCloseAwaitingAcceptanceEnabled) {
                parts.push(`автозакрытие приемки: ${deadlines.autoCloseAwaitingAcceptanceDays || '?'} дн.`);
            }

            if (this.hasReturnFlow(item)) {
                parts.push('возврат на доработку разрешен');
            }

            return parts.join(', ');
        },
        getRequiredFilesSummary(item) {
            if (!item) {
                return '';
            }

            const parts = [];

            if (item.attachments_required) {
                parts.push('вложения обязательны');
            }

            if (this.isRequiredAttachmentsEnabled(item)) {
                const count = this.getRequiredAttachmentRulesCount(item);
                parts.push(`${count} правил(а)`);
                if (this.isRequiredAttachmentsPreviewEnabled(item)) {
                    parts.push('видны в списке');
                }
            }

            return parts.join(', ');
        },
        getEditingSummary(item) {
            const workflowConfig = this.getWorkflowConfig(item);
            const editing = workflowConfig.requestEditing || workflowConfig.request_editing || {};
            const parts = [];

            if (editing.allowAfterApproval) {
                parts.push('после согласования');
            }
            if (editing.allowAfterExecutionStartWithoutApprovers) {
                parts.push('после старта без согласующих');
            }
            if (Array.isArray(editing.editorRoles) && editing.editorRoles.length) {
                parts.push(`роли: ${editing.editorRoles.join(', ')}`);
            }

            return parts.join(', ');
        },
        getParticipantRows(item) {
            const rows = [
                {
                    key: 'initiator',
                    label: 'Создает',
                    icon: 'fas fa-user-plus',
                    text: this.formatInitiatorConfig(item && item.initiator_config),
                    userIds: this.collectSpecificUserIds(item && item.initiator_config)
                }
            ];

            if (this.hasParticipantsConfig(item && item.approvers)) {
                rows.push({
                    key: 'approvers',
                    label: 'Согласуют',
                    icon: 'fas fa-user-check',
                    text: this.formatMultipleParticipants(item.approvers),
                    userIds: this.collectSpecificUserIds(item.approvers)
                });
            } else {
                rows.push({
                    key: 'approvers',
                    label: 'Согласуют',
                    icon: 'fas fa-user-check',
                    text: 'Без согласования',
                    userIds: []
                });
            }

            if (this.hasParticipantsConfig(item && item.observers)) {
                rows.push({
                    key: 'observers',
                    label: 'Наблюдают',
                    icon: 'fas fa-eye',
                    text: this.formatMultipleParticipants(item.observers, { usePriorityLabels: false }),
                    userIds: this.collectSpecificUserIds(item.observers)
                });
            }

            if (this.hasParticipantsConfig(item && item.executors) || item.executor_assignment_manual) {
                const executorText = item.executor_assignment_manual
                    ? `Ручное назначение${this.hasParticipantsConfig(item.executors) ? `: ${this.formatMultipleParticipants(item.executors)}` : ''}`
                    : this.formatMultipleParticipants(item.executors);
                rows.push({
                    key: 'executors',
                    label: 'Исполняют',
                    icon: 'fas fa-user-cog',
                    text: executorText,
                    userIds: this.collectSpecificUserIds(item.executors)
                });
            }

            if (item && item.executor_assignment_manual && item.executor_assigner) {
                rows.push({
                    key: 'assigner',
                    label: 'Назначает',
                    icon: 'fas fa-user-shield',
                    text: this.formatParticipantConfig(item.executor_assigner),
                    userIds: this.collectSpecificUserIds(item.executor_assigner)
                });
            }

            return rows;
        },
        collectSpecificUserIds(config) {
            const result = [];
            const visit = source => {
                if (!source) {
                    return;
                }
                if (Array.isArray(source)) {
                    source.forEach(visit);
                    return;
                }
                if (Array.isArray(source.participants)) {
                    source.participants.forEach(visit);
                    return;
                }
                if (source.type === 'specific_users' && Array.isArray(source.user_ids)) {
                    source.user_ids.forEach(id => {
                        const numericId = Number(id);
                        if (Number.isFinite(numericId) && numericId > 0) {
                            result.push(numericId);
                        }
                    });
                }
            };

            visit(config);
            return Array.from(new Set(result));
        },
        hasParticipantUserTooltip(row) {
            return Boolean(row && Array.isArray(row.userIds) && row.userIds.length);
        },
        loadParticipantUsers(row) {
            if (!this.hasParticipantUserTooltip(row)) {
                return;
            }
            this.ensureUsersLoaded();
        },
        ensureUsersLoaded() {
            if (this.allUsers.length) {
                return Promise.resolve(this.allUsers);
            }

            const storeUsers = this.$store.state.users || [];
            if (storeUsers.length) {
                this.allUsers = storeUsers;
                return Promise.resolve(this.allUsers);
            }

            if (this.usersLoadPromise) {
                return this.usersLoadPromise;
            }

            this.usersLoading = true;
            this.usersLoadError = null;
            this.usersLoadPromise = this.$store.dispatch('getUsers', { full: true })
                .then(() => {
                    this.allUsers = this.$store.state.users || [];
                    return this.allUsers;
                })
                .catch(error => {
                    this.usersLoadError = error;
                    return [];
                })
                .finally(() => {
                    this.usersLoading = false;
                    this.usersLoadPromise = null;
                });

            return this.usersLoadPromise;
        },
        getParticipantTooltipUsers(row) {
            if (!this.hasParticipantUserTooltip(row)) {
                return [];
            }

            return row.userIds.map(id => {
                const user = this.allUsers.find(item => Number(this.getUserId(item)) === Number(id));
                return {
                    id,
                    name: user ? this.getUserDisplayName(user) : `#${id}`
                };
            });
        },
        getUserId(user) {
            return user && (user.user_id || user.userId || user.id);
        },
        getUserDisplayName(user) {
            if (!user) {
                return '';
            }

            const name = user.full_name ||
                [user.last_name, user.first_name].filter(Boolean).join(' ').trim() ||
                [user.lastName, user.firstName].filter(Boolean).join(' ').trim();
            const login = user.login ? ` (${user.login})` : '';
            const phone = user.phone ? `, ${this.$formatPhone(user.phone)}` : '';
            return `${name || `#${this.getUserId(user)}`}${login}${phone}`;
        },
        formatParticipantConfig(config) {
            if (!config) return 'Не настроено';

            if (Array.isArray(config.participants)) {
                return this.formatMultipleParticipants(config, { usePriorityLabels: false });
            }
            
            switch (config.type) {
                case 'all':
                    return 'Все пользователи';
                case 'specific_users':
                    return `Конкретные пользователи (${config.user_ids?.length || 0})`;
                case 'department': {
                    const dept = this.allDepartments.find(d => d.value === config.department_id);
                    return dept ? dept.text : 'Отдел';
                }
                case 'object_role': {
                    if (!config.roles || !Array.isArray(config.roles) || config.roles.length === 0) {
                        return 'Роль на объекте (не выбрано)';
                    }
                    
                    const roleLabels = {
                        'curator': 'Куратор',
                        'main_staff': 'Прораб',
                        'pto': 'ПТО',
                        'has_access': 'Имеющий доступ'
                    };
                    
                    const labels = config.roles
                        .map(role => roleLabels[role] || role)
                        .filter(Boolean);
                    
                    return labels.length > 0 ? labels.join(', ') : 'Роль на объекте (не выбрано)';
                }
                default:
                    return 'Не настроено';
            }
        },
        formatMultipleParticipants(participantsConfig, options = {}) {
            if (!participantsConfig) return 'Не настроено';
            
            const participants = Array.isArray(participantsConfig)
                ? participantsConfig
                : (participantsConfig.participants || []);
            const allowAny = participantsConfig.allow_any_department_employee;
            const usePriorityLabels = options.usePriorityLabels !== false;
            
            if (!participants.length) return 'Не настроено';
            
            let result = '';
            if (participants.length === 1) {
                result = this.formatParticipantConfig(participants[0]);
            } else if (!usePriorityLabels) {
                result = participants.map(p => this.formatParticipantConfig(p)).join('; ');
            } else {
                const formatted = participants.map((p, index) => {
                    const label = index === 0 ? 'Основной' : `Запасной ${index}`;
                    return `${label}: ${this.formatParticipantConfig(p)}`;
                });
                result = formatted.join('; ');
            }
            
            if (allowAny) {
                result += ' + любой из отдела';
            }
            
            return result;
        },
        formatInitiatorConfig(config) {
            return this.formatParticipantConfig(config);
        },
        hasParticipantsConfig(participantsConfig) {
            if (!participantsConfig) return false;

            if (Array.isArray(participantsConfig)) {
                return participantsConfig.length > 0;
            }

            return Array.isArray(participantsConfig.participants) && participantsConfig.participants.length > 0;
        },
        matchesProcessFilter(item, filter) {
            switch (filter) {
                case 'all':
                    return true;
                case 'manual_executor':
                    return Boolean(item && item.executor_assignment_manual);
                case 'building_process':
                    return this.getBuildingProcessConfig(item).enabled;
                case 'item_catalog':
                    return this.isItemCatalogEnabled(item);
                case 'mechanic_action':
                    return this.isMechanicAssetActionEnabled(item);
                case 'fuel_card_action':
                    return this.isFuelCardActionEnabled(item);
                case 'linked_requests':
                    return this.isLinkedRequestsEnabled(item);
                case 'required_attachments':
                    return this.isRequiredAttachmentsEnabled(item);
                case 'returns':
                    return this.hasReturnFlow(item);
                case 'automation':
                    return this.hasAutoTransitions(item) || this.hasDeadlineAutomation(item);
                default:
                    return true;
            }
        },
        getSearchText(item) {
            const values = [
                item && item.id ? `#${item.id}` : null,
                item && item.id,
                item && item.name,
                item && item.description,
                item && item.category,
                this.getCategoryText(item && item.category),
                item && item.is_active ? 'активен' : 'неактивен',
                this.getProcessHeadline(item),
                ...this.getProcessBadges(item).map(badge => badge.text)
            ];

            return this.normalizeSearch(values.filter(Boolean).join(' '));
        },
        getSearchTokens(item) {
            return this.getSearchWords(this.getSearchText(item));
        },
        getSearchWords(value, options = {}) {
            const words = this.normalizeSearch(value)
                .split(/[^0-9a-zа-я]+/i)
                .filter(Boolean);
            if (!options.ignoreStopWords) {
                return words;
            }
            const filtered = words.filter(word => !SEARCH_STOP_WORDS.has(word));
            return filtered.length ? filtered : words;
        },
        matchesSearchWord(tokens, word) {
            const needle = this.normalizeSearch(word);
            if (!needle) {
                return true;
            }
            if (needle.length <= 2) {
                return tokens.some(token => token === needle);
            }
            return tokens.some(token =>
                token === needle ||
                token.startsWith(needle) ||
                (needle.length >= 4 && token.indexOf(needle) !== -1)
            );
        },
        normalizeSearch(value) {
            return String(value || '')
                .toLowerCase()
                .replace(/ё/g, 'е')
                .replace(/\s+/g, ' ')
                .trim();
        },
        getOptionText(options, value) {
            const option = options.find(item => item.value === value);
            return option ? option.text : value;
        },
        clearSearch() {
            this.filters.q = '';
        },
        clearFilterItem(key) {
            if (key === 'q') {
                this.filters.q = '';
            } else if (this.filters[key] !== undefined) {
                this.filters[key] = 'all';
            }
        },
        resetFilters() {
            this.filters = getDefaultFilters();
        },
        isHighlightTargetRequestType(item) {
            return Boolean(item && this.highlightedRequestTypeId && Number(item.id) === Number(this.highlightedRequestTypeId));
        },
        requestTypeRowClass(item) {
            if (!item) {
                return '';
            }

            return {
                'request-type-row--inactive': !item.is_active,
                'request-type-row--return-flow': item.is_active && this.hasReturnFlow(item),
                'request-type-row--highlight-target': this.isHighlightTargetRequestType(item)
            };
        },
        openCreateModal() {
            this.saveListReturnState(null);
            this.$router.push({ name: 'create-request-type' });
        },
        async openEditModal(item) {
            if (!item) {
                return;
            }
            await this.prepareReturnToList(item.id);
            this.$router.push({ name: 'edit-request-type', params: { id: item.id } });
        },
        saveListReturnState(requestTypeId) {
            try {
                sessionStorage.setItem(LIST_RETURN_SESSION_KEY, JSON.stringify({
                    requestTypeId: requestTypeId ? Number(requestTypeId) : null,
                    filters: { ...this.filters },
                    page: this.currentPage,
                    perPage: this.perPage,
                    savedAt: Date.now()
                }));
            } catch (e) { /* sessionStorage недоступен */ }
        },
        saveListState() {
            try {
                sessionStorage.setItem(LIST_STATE_SESSION_KEY, JSON.stringify({
                    filters: { ...this.filters },
                    page: this.currentPage,
                    perPage: this.perPage,
                    savedAt: Date.now()
                }));
            } catch (e) { /* sessionStorage недоступен */ }
        },
        readListState() {
            return readStoredListState(LIST_STATE_SESSION_KEY);
        },
        readListReturnState(requestTypeId) {
            const data = readStoredListState(LIST_RETURN_SESSION_KEY);
            if (!data) {
                return null;
            }
            if (
                requestTypeId &&
                data.requestTypeId &&
                Number(data.requestTypeId) !== Number(requestTypeId)
            ) {
                return null;
            }
            return data;
        },
        clearListReturnState() {
            try { sessionStorage.removeItem(LIST_RETURN_SESSION_KEY); } catch (e) { /* ignore */ }
        },
        restoreListState(state) {
            if (!state) {
                return;
            }
            const page = Number(state.page);
            const perPage = Number(state.perPage);
            this.restoringListState = true;
            this.filters = {
                ...getDefaultFilters(),
                ...(state.filters || {})
            };
            if (this.pageSizeOptions.some(option => Number(option.value) === perPage)) {
                this.perPage = perPage;
            }
            this.currentPage = Number.isInteger(page) && page > 0 ? page : 1;
            this.$nextTick(() => {
                this.restoringListState = false;
            });
        },
        clampCurrentPage() {
            const maxPage = Math.max(1, Math.ceil(this.totalRows / this.perPage));
            if (this.currentPage > maxPage) {
                this.currentPage = maxPage;
            }
            if (this.currentPage < 1) {
                this.currentPage = 1;
            }
        },
        ensureHighlightedRequestTypePage(requestTypeId) {
            const id = Number(requestTypeId);
            if (!id) {
                return false;
            }
            const index = this.filteredRequestTypes.findIndex(item => Number(item.id) === id);
            if (index === -1) {
                this.clampCurrentPage();
                return false;
            }
            this.currentPage = Math.floor(index / this.perPage) + 1;
            return true;
        },
        getRouteHighlightId() {
            const highlight = this.$route.query && this.$route.query.highlight;
            const id = Number(highlight);
            return Number.isInteger(id) && id > 0 ? id : null;
        },
        async markCurrentHistoryForReturn(requestTypeId) {
            if (!requestTypeId || this.$route.name !== 'request-types') {
                return;
            }
            const currentHighlight = this.$route.query && this.$route.query.highlight;
            if (String(currentHighlight || '') === String(requestTypeId)) {
                return;
            }
            await this.$router.replace({
                name: 'request-types',
                query: {
                    ...this.$route.query,
                    highlight: String(requestTypeId)
                }
            }).catch(() => {});
        },
        async prepareReturnToList(requestTypeId) {
            this.saveListReturnState(requestTypeId);
            this.saveListState();
            await this.markCurrentHistoryForReturn(requestTypeId);
        },
        isVisibleHighlightTarget(target) {
            if (!target || typeof target.getClientRects !== 'function') {
                return false;
            }
            if (!target.getClientRects().length) {
                return false;
            }
            if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
                return true;
            }
            const style = window.getComputedStyle(target);
            return style.display !== 'none' && style.visibility !== 'hidden';
        },
        getHighlightTargetElement() {
            if (!this.$el) {
                return null;
            }
            const candidates = [
                ...this.$el.querySelectorAll('.request-type-mobile-card--highlight-target'),
                ...this.$el.querySelectorAll('.request-types-table tbody tr.request-type-row--highlight-target')
            ];
            return candidates.find(target => this.isVisibleHighlightTarget(target)) || candidates[0] || null;
        },
        applyHighlightBlink(target) {
            if (!target || !target.classList) {
                return;
            }
            const cls = target.classList.contains('request-type-mobile-card')
                ? 'request-type-mobile-card--highlighted'
                : 'request-type-row--highlighted';
            target.classList.remove(cls);
            void target.offsetWidth;
            target.classList.add(cls);
            const onEnd = () => {
                target.classList.remove(cls);
                target.removeEventListener('animationend', onEnd);
            };
            target.addEventListener('animationend', onEnd);
        },
        async scrollToHighlightedRequestType(requestTypeId) {
            if (!requestTypeId) {
                return;
            }
            await this.$nextTick();
            await this.$nextTick();
            const target = this.getHighlightTargetElement();
            if (target && typeof target.scrollIntoView === 'function') {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => {
                    if (this._isDestroyed) {
                        return;
                    }
                    this.applyHighlightBlink(target);
                }, 550);
            }
        },
        async restoreFromHighlight(requestTypeId) {
            const id = Number(requestTypeId);
            if (!id) {
                await this.fetchTypes();
                return;
            }
            this.highlightedRequestTypeId = id;
            const state = this.readListReturnState(id);
            const fallbackState = state || this.readListState();
            if (fallbackState) {
                this.restoreListState(fallbackState);
                if (state) {
                    this.clearListReturnState();
                }
            }
            await this.fetchTypes();
            this.ensureHighlightedRequestTypePage(id);
            this.saveListState();
            await this.scrollToHighlightedRequestType(id);
        },
        restorePendingListStateWithoutHighlight() {
            const returnState = this.readListReturnState(null);
            const state = returnState || this.readListState();
            if (!state || state.requestTypeId) {
                return false;
            }
            this.restoreListState(state);
            if (returnState) {
                this.clearListReturnState();
            }
            return true;
        },
        cloneJsonValue(value, fallback) {
            const source = value === undefined || value === null ? fallback : value;
            return JSON.parse(JSON.stringify(source));
        },
        buildCopyPayload(item) {
            return {
                name: `${item.name} (копия)`,
                description: item.description || '',
                category: item.category,
                is_active: false,
                initiator_config: this.cloneJsonValue(item.initiator_config, { type: 'all' }),
                approvers: this.cloneJsonValue(item.approvers, { participants: [], allow_any_department_employee: false }),
                observers: this.cloneJsonValue(item.observers, { participants: [], allow_any_department_employee: false }),
                executors: this.cloneJsonValue(item.executors, { participants: [], allow_any_department_employee: false }),
                executor_assignment_manual: item.executor_assignment_manual || false,
                executor_assigner: this.cloneJsonValue(item.executor_assigner, { type: 'all' }),
                custom_fields: this.cloneJsonValue(item.custom_fields, []),
                allow_attachments: item.allow_attachments !== undefined ? item.allow_attachments : true,
                attachments_required: item.attachments_required || false,
                max_file_size: item.max_file_size || 10,
                max_files_count: item.max_files_count || 5,
                allowed_file_types: this.cloneJsonValue(item.allowed_file_types, []),
                allow_comments: item.allow_comments !== undefined ? item.allow_comments : true,
                comment_permissions: item.comment_permissions || 'all_participants',
                allow_comment_attachments: item.allow_comment_attachments !== undefined ? item.allow_comment_attachments : true,
                max_comment_file_size: item.max_comment_file_size || 5,
                max_comment_files_count: item.max_comment_files_count || 3,
                submission_deadline_days: item.submission_deadline_days || 4,
                is_emergency: item.is_emergency || false,
                requires_justification_if_late: item.requires_justification_if_late !== undefined ? item.requires_justification_if_late : true,
                requires_title: item.requires_title !== undefined ? item.requires_title : true,
                status_config: this.cloneJsonValue(item.status_config, {}),
                workflow_config: this.cloneJsonValue(item.workflow_config, {}),
                late_approval_config: this.cloneJsonValue(item.late_approval_config, {
                    enabled: false,
                    approvers: {
                        participants: [],
                        allow_any_department_employee: false
                    }
                })
            };
        },
        copyRequestType(item) {
            if (this.copyingRequestTypeId) {
                return;
            }

            this.$bvModal.msgBoxConfirm(
                `Создать неактивную копию типа заявки "${item.name}"?`,
                { contentClass: 'argus-glass-dialog',
                    title: 'Копирование типа заявки',
                    okVariant: 'primary',
                    okTitle: 'Копировать',
                    cancelTitle: 'Отмена'
                }
            ).then(async confirmed => {
                if (!confirmed) return;

                this.copyingRequestTypeId = item.id;
                try {
                    const created = await this.$store.dispatch('requests_createRequestType', this.buildCopyPayload(item));
                    await this.fetchTypes();
                    this.$bvToast.toast('Копия типа заявки создана и отключена', {
                        title: 'Успех',
                        variant: 'success',
                        solid: true
                    });

                    if (created && created.id) {
                        await this.prepareReturnToList(created.id);
                        this.$router.push({ name: 'edit-request-type', params: { id: created.id } });
                    }
                } catch (e) {
                    console.error('Ошибка копирования типа заявки:', e);
                    this.$bvToast.toast('Ошибка копирования типа заявки', { title: 'Ошибка', variant: 'danger', solid: true });
                } finally {
                    this.copyingRequestTypeId = null;
                }
            });
        },
        deleteRequestType(item) {
            this.$bvModal.msgBoxConfirm(
                `Вы уверены, что хотите удалить тип заявки "${item.name}"?`,
                { contentClass: 'argus-glass-dialog',
                    title: 'Подтверждение удаления',
                    okVariant: 'danger',
                    okTitle: 'Удалить',
                    cancelTitle: 'Отмена'
                }
            ).then(async confirmed => {
                if (!confirmed) return;
                try {
                    await this.$store.dispatch('requests_deleteRequestType', item.id);
                    const index = this.requestTypes.findIndex(rt => rt.id === item.id);
                    if (index !== -1) this.requestTypes.splice(index, 1);
                    this.$bvToast.toast('Тип заявки удален', { title: 'Успех', variant: 'success', solid: true });
                } catch (e) {
                    console.error('Ошибка удаления типа заявки:', e);
                    this.$bvToast.toast('Ошибка удаления типа заявки', { title: 'Ошибка', variant: 'danger', solid: true });
                }
            });
        },
        async loadDepartments() {
            try {
                let departments = this.$store.state.departments || [];
                if (!departments.length) {
                    const loadedDepartments = await this.$store.dispatch('getDepartments');
                    departments = Array.isArray(loadedDepartments)
                        ? loadedDepartments
                        : (this.$store.state.departments || []);
                }

                this.allDepartments = departments
                    .filter(dept => dept && dept.is_active !== false)
                    .map(dept => ({
                        value: dept.id,
                        text: dept.name
                    }))
                    .filter(dept => dept.value && dept.text)
                    .sort((a, b) => a.text.localeCompare(b.text));
            } catch (error) {
                console.error('Ошибка загрузки отделов:', error);
                this.$bvToast.toast('Не удалось загрузить список отделов', {
                    title: 'Ошибка',
                    variant: 'warning',
                    solid: true
                });
            }
        }
    },
    async mounted() {
        this.hydrateRequestTypesFromStore();
        this.loadDepartments();
        const highlightId = this.getRouteHighlightId();
        if (highlightId) {
            await this.restoreFromHighlight(highlightId);
        } else {
            const restored = this.restorePendingListStateWithoutHighlight();
            await this.fetchTypes();
            if (restored) {
                this.clampCurrentPage();
            }
        }
    },
    activated() {
        const highlightId = this.getRouteHighlightId();
        if (highlightId) {
            this.restoreFromHighlight(highlightId);
        } else {
            const restored = this.restorePendingListStateWithoutHighlight();
            this.fetchTypes().then(() => {
                if (restored) {
                    this.clampCurrentPage();
                }
            });
        }
    },
    beforeDestroy() {
        this.saveListState();
    },
}
</script>

<style scoped>
.request-types-page {
    min-width: 0;
}

.request-types-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 10px;
}

.request-types-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 1.7fr) minmax(150px, 0.8fr) minmax(130px, 0.65fr) minmax(190px, 0.9fr);
    gap: 8px;
    align-items: center;
    margin-bottom: 8px;
}

.request-types-search {
    min-width: 0;
}

.request-types-active-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    margin: 4px 0 8px;
    padding: 6px 8px;
    border: 1px solid #ffe08a;
    border-radius: 5px;
    background: #fff8e1;
    color: #5c4300;
    font-size: 13px;
}

.request-types-active-filters__label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    font-weight: 700;
}

.request-types-active-filter,
.request-types-active-filters__reset {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 28px;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

.request-types-active-filter {
    max-width: 100%;
    border: 1px solid #6c757d;
    background: #6c757d;
    color: #fff;
}

.request-types-active-filter__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.request-types-active-filters__reset {
    border: 1px solid #b38300;
    background: rgba(255, 255, 255, .72);
    color: #5c4300;
}

.request-types-table {
    margin-bottom: 0;
}

.request-types-table ::v-deep thead th {
    border-top: 1px solid #dee2e6;
    border-bottom-width: 1px;
    background: #f8f9fa;
    color: #495057;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0;
    white-space: nowrap;
}

.request-types-table ::v-deep tbody td {
    vertical-align: top;
    border-top-color: #edf0f2;
}

.request-types-table ::v-deep tbody tr:hover > td {
    background-color: #eef4ff;
}

.request-types-table ::v-deep tbody tr.request-type-row--inactive > td {
    background-color: #f8f9fa;
    color: #6c757d;
}

.request-types-table ::v-deep tbody tr.request-type-row--return-flow > td:first-child {
    box-shadow: inset 4px 0 0 #ffc107;
}

@keyframes request-type-highlight-blink-anim {
    0%,
    100% {
        background-color: inherit;
        box-shadow: none;
    }
    50% {
        background-color: #e7f1ff;
        box-shadow:
            inset 0 0 0 2px rgba(13, 110, 253, 0.78),
            0 0 20px rgba(13, 110, 253, 0.22);
    }
}

.request-types-table ::v-deep tbody tr.request-type-row--highlighted > td {
    animation: request-type-highlight-blink-anim 0.34s ease-in-out 5;
    background-color: #e7f1ff;
}

.request-types-table ::v-deep tbody tr.request-type-row--highlighted > td:first-child {
    box-shadow: inset 4px 0 0 #0d6efd;
}

.request-types-table ::v-deep .request-type-name-cell {
    min-width: 280px;
}

.request-types-table ::v-deep .request-type-participants-cell {
    min-width: 260px;
    width: 30%;
}

.request-types-table ::v-deep .request-type-workflow-cell {
    min-width: 360px;
    width: 45%;
}

.request-types-table ::v-deep .request-type-actions-cell {
    width: 126px;
    min-width: 126px;
    white-space: nowrap;
}

.request-type-main {
    display: grid;
    grid-template-columns: 12px minmax(0, 1fr);
    gap: 8px;
    min-width: 0;
}

.request-type-color-dot {
    width: 10px;
    height: 100%;
    min-height: 42px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
}

.request-type-main__body {
    min-width: 0;
}

.request-type-main__title-row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.request-type-list-icon {
    flex: 0 0 18px;
    width: 18px;
    color: #495057;
    font-size: 0.95rem;
    line-height: 1rem;
    text-align: center;
}

.request-type-main__title {
    min-width: 0;
    color: #212529;
    font-weight: 700;
    line-height: 1.2;
    overflow-wrap: anywhere;
    white-space: normal;
}

a.request-type-main__title:hover {
    color: #0056b3;
    text-decoration: none;
}

.request-type-main__status {
    flex: 0 0 auto;
}

.request-type-main__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 5px 10px;
    margin-top: 3px;
    color: #6c757d;
    font-size: 0.78rem;
    line-height: 1.25;
}

.request-type-main__description {
    display: -webkit-box;
    overflow: hidden;
    margin-top: 5px;
    color: #6c757d;
    font-size: 0.8rem;
    line-height: 1.25;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}

.request-type-participants {
    display: grid;
    gap: 6px;
    min-width: 0;
}

.request-type-participant-row {
    position: relative;
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    gap: 7px;
    align-items: flex-start;
    min-width: 0;
    padding: 6px 7px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background: #f8f9fa;
}

.request-type-participant-row__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #e9ecef;
    color: #495057;
    font-size: 0.78rem;
    line-height: 1;
}

.request-type-participant-row--initiator .request-type-participant-row__icon {
    background: #e7f1ff;
    color: #084298;
}

.request-type-participant-row--approvers .request-type-participant-row__icon {
    background: #d1e7dd;
    color: #0f5132;
}

.request-type-participant-row--observers .request-type-participant-row__icon {
    background: #cff4fc;
    color: #055160;
}

.request-type-participant-row--executors .request-type-participant-row__icon,
.request-type-participant-row--assigner .request-type-participant-row__icon {
    background: #fff3cd;
    color: #664d03;
}

.request-type-participant-row__content {
    min-width: 0;
}

.request-type-participant-row__label {
    color: #6c757d;
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1.25;
    text-transform: uppercase;
}

.request-type-participant-row__value-wrap {
    position: relative;
    display: inline-block;
    max-width: 100%;
    min-width: 0;
}

.request-type-participant-row__value-wrap--interactive {
    cursor: help;
}

.request-type-participant-row__value-wrap--interactive .request-type-participant-row__value {
    color: #0056b3;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
}

.request-type-participant-row__value {
    min-width: 0;
    overflow-wrap: anywhere;
    color: #212529;
    font-size: 0.82rem;
    line-height: 1.3;
}

.request-type-participant-tooltip {
    position: absolute;
    z-index: 30;
    top: calc(100% + 5px);
    left: 0;
    display: none;
    width: max-content;
    min-width: 220px;
    max-width: min(320px, calc(100vw - 32px));
    padding: 8px 10px;
    border-radius: 5px;
    background: #212529;
    color: #fff;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
    font-size: 0.78rem;
    line-height: 1.25;
}

.request-type-participant-tooltip::before {
    content: "";
    position: absolute;
    top: -5px;
    left: 14px;
    width: 10px;
    height: 10px;
    background: #212529;
    transform: rotate(45deg);
}

.request-type-participant-row__value-wrap--interactive:hover .request-type-participant-tooltip {
    display: block;
}

.request-type-participant-tooltip__title {
    margin-bottom: 5px;
    color: #ced4da;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
}

.request-type-participant-tooltip__list {
    display: grid;
    gap: 4px;
}

.request-type-participant-tooltip__user {
    overflow-wrap: anywhere;
}

.request-type-participant-tooltip__muted {
    color: #ced4da;
}

.request-type-workflow {
    display: grid;
    gap: 6px;
    min-width: 0;
}

.request-type-workflow__headline {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    color: #212529;
    font-size: 0.86rem;
    font-weight: 700;
    line-height: 1.25;
}

.request-type-workflow__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.request-type-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 22px;
    max-width: 100%;
    padding: 2px 7px;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 0.74rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

.request-type-chip--primary {
    background: #e7f1ff;
    border-color: #9ec5fe;
    color: #084298;
}

.request-type-chip--secondary {
    background: #f1f3f5;
    border-color: #ced4da;
    color: #495057;
}

.request-type-chip--success {
    background: #d1e7dd;
    border-color: #a3cfbb;
    color: #0f5132;
}

.request-type-chip--warning {
    background: #fff3cd;
    border-color: #ffda6a;
    color: #664d03;
}

.request-type-chip--danger {
    background: #f8d7da;
    border-color: #f1aeb5;
    color: #842029;
}

.request-type-chip--info {
    background: #cff4fc;
    border-color: #9eeaf9;
    color: #055160;
}

.request-type-workflow__details {
    display: grid;
    gap: 3px;
    min-width: 0;
}

.request-type-workflow-detail {
    display: grid;
    grid-template-columns: 78px minmax(0, 1fr);
    gap: 6px;
    min-width: 0;
    font-size: 0.8rem;
    line-height: 1.3;
}

.request-type-workflow-detail__label {
    color: #6c757d;
    font-weight: 700;
}

.request-type-workflow-detail__value {
    min-width: 0;
    overflow-wrap: anywhere;
}

.request-type-actions {
    display: inline-flex;
    justify-content: center;
    gap: 5px;
}

.request-type-action-button {
    width: 30px;
    height: 30px;
    padding: 0;
    line-height: 1;
}

.request-types-mobile-list {
    display: none;
}

.request-type-mobile-card {
    position: relative;
    overflow: visible;
}

.request-type-mobile-card--inactive {
    opacity: .82;
}

.request-type-mobile-card--return-flow {
    box-shadow: inset 4px 0 0 #ffc107;
}

.request-type-mobile-card--highlighted {
    animation: request-type-highlight-blink-anim 0.34s ease-in-out 5;
    box-shadow:
        inset 4px 0 0 #0d6efd,
        0 0 0 2px rgba(13, 110, 253, 0.42);
}

.request-type-mobile-card__accent {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
}

.request-type-mobile-card__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    padding: 9px 10px 8px 14px;
    border-bottom: 1px solid #e9ecef;
    background: #f8f9fa;
}

.request-type-mobile-card__title-block {
    min-width: 0;
}

.request-type-mobile-card__title-row {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    min-width: 0;
}

.request-type-mobile-card__title {
    min-width: 0;
    overflow-wrap: anywhere;
    color: #212529;
    font-weight: 700;
    line-height: 1.25;
}

a.request-type-mobile-card__title:hover {
    color: #0056b3;
    text-decoration: none;
}

.request-type-mobile-card__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 5px 10px;
    margin-top: 4px;
    color: #6c757d;
    font-size: 0.78rem;
}

.request-type-mobile-card__description {
    padding: 8px 10px 0 14px;
    color: #6c757d;
    font-size: 0.82rem;
    line-height: 1.3;
}

.request-type-mobile-card__section {
    padding: 9px 10px 0 14px;
}

.request-type-mobile-card__section-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
    color: #495057;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.2;
    text-transform: uppercase;
}

.request-type-mobile-card__footer {
    display: flex;
    justify-content: flex-end;
    gap: 6px;
    padding: 10px 10px 10px 14px;
}

.request-type-mobile-card__footer .btn {
    width: 32px;
    height: 30px;
    padding: 0;
    line-height: 1;
}

.request-types-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #e9ecef;
}

.request-types-footer__page-size {
    display: flex;
    align-items: center;
    gap: 7px;
}

.request-types-page-size {
    width: 78px;
}

@media (max-width: 992px) {
    .request-types-toolbar {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 576px) {
    .request-types-header {
        align-items: stretch;
        flex-direction: column;
    }

    .request-types-toolbar {
        grid-template-columns: 1fr;
    }

    .request-types-active-filters {
        align-items: stretch;
    }

    .request-types-active-filters__label,
    .request-types-active-filters__reset {
        width: 100%;
    }

    .request-type-main__title-row {
        align-items: flex-start;
        flex-wrap: wrap;
    }

    .request-type-main__title {
        white-space: normal;
    }

    .request-type-workflow-detail {
        grid-template-columns: 1fr;
        gap: 2px;
    }

    .request-type-participant-tooltip {
        right: 0;
        left: auto;
        min-width: min(260px, calc(100vw - 44px));
    }

    .request-type-participant-tooltip::before {
        right: 18px;
        left: auto;
    }

    .request-types-footer {
        align-items: stretch;
        flex-direction: column;
    }

    .request-types-footer__page-size {
        justify-content: space-between;
    }
}

@media (max-width: 767.98px) {
    .request-types-table {
        display: none;
    }

    .request-types-mobile-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding-bottom: 4px;
    }

    .request-type-participants--mobile .request-type-participant-row {
        overflow: visible;
    }

    .request-type-participants--mobile .request-type-participant-row__value-wrap {
        position: static;
    }

    .request-type-participants--mobile .request-type-participant-tooltip {
        right: 0;
        left: 0;
        width: auto;
        min-width: 0;
        max-width: none;
    }

    .request-type-participants--mobile .request-type-participant-tooltip::before {
        right: auto;
        left: 86px;
    }
}
</style>
